import type { CSSProperties } from 'react';
import type { ModelViewerSettings } from '@/types/Annotation.model';

export type ModelViewerToneMapping =
  | 'auto'
  | 'commerce'
  | 'neutral'
  | 'aces'
  | 'agx'
  | 'reinhard'
  | 'cineon'
  | 'linear'
  | 'none';

export type ModelViewerEnvironmentPreset = 'neutral' | 'legacy' | 'auto';

export type ModelViewerSkyboxPreset = 'none' | 'neutral' | 'legacy' | 'auto';

export type ModelViewerRotationSpeed = 'auto' | '10deg' | '30deg' | '60deg' | '90deg';

export type ModelViewerBackgroundPreset =
  | 'transparent'
  | '#ffffff'
  | '#f0f0f0'
  | '#e8e8e8'
  | '#2d2d2d'
  | '#000000';

export interface ModelViewerPresentationSettings {
  shadowIntensity: number;
  shadowSoftness: number;
  exposure: number;
  toneMapping: ModelViewerToneMapping;
  environmentImage: ModelViewerEnvironmentPreset;
  skyboxImage: ModelViewerSkyboxPreset;
  skyboxHeight: number;
  backgroundColor: ModelViewerBackgroundPreset;
  autoRotate: boolean;
  autoRotateDelay: number;
  rotationPerSecond: ModelViewerRotationSpeed;
  variantName: string;
}

export const DEFAULT_MODEL_VIEWER_PRESENTATION: ModelViewerPresentationSettings = {
  shadowIntensity: 1,
  shadowSoftness: 0.75,
  exposure: 1,
  toneMapping: 'commerce',
  environmentImage: 'neutral',
  skyboxImage: 'none',
  skyboxHeight: 0,
  backgroundColor: 'transparent',
  autoRotate: false,
  autoRotateDelay: 3000,
  rotationPerSecond: 'auto',
  variantName: '',
};

export const MODEL_VIEWER_TONE_MAPPING_OPTIONS: ModelViewerToneMapping[] = [
  'auto',
  'commerce',
  'neutral',
  'aces',
  'agx',
  'reinhard',
  'cineon',
  'linear',
  'none',
];

export const MODEL_VIEWER_ENVIRONMENT_OPTIONS: ModelViewerEnvironmentPreset[] = [
  'neutral',
  'legacy',
  'auto',
];

export const MODEL_VIEWER_SKYBOX_OPTIONS: ModelViewerSkyboxPreset[] = [
  'none',
  'neutral',
  'legacy',
  'auto',
];

export const MODEL_VIEWER_ROTATION_SPEED_OPTIONS: ModelViewerRotationSpeed[] = [
  'auto',
  '10deg',
  '30deg',
  '60deg',
  '90deg',
];

export const MODEL_VIEWER_BACKGROUND_OPTIONS: ModelViewerBackgroundPreset[] = [
  'transparent',
  '#ffffff',
  '#f0f0f0',
  '#e8e8e8',
  '#2d2d2d',
  '#000000',
];

/** i18n slug per background preset (# hex keys break i18next dot paths). */
export const MODEL_VIEWER_BACKGROUND_I18N_KEYS: Record<ModelViewerBackgroundPreset, string> = {
  transparent: 'transparent',
  '#ffffff': 'white',
  '#f0f0f0': 'lightGray',
  '#e8e8e8': 'gray',
  '#2d2d2d': 'darkGray',
  '#000000': 'black',
};

/** Official model-viewer HDR exports (shared-assets), served from /public/environments. */
const MODEL_VIEWER_SKYBOX_HDR_FILES: Record<'neutral' | 'legacy', string> = {
  neutral: 'neutral.hdr',
  legacy: 'lightroom_14b.hdr',
};

function resolveSkyboxHdrUrl(preset: 'neutral' | 'legacy'): string {
  const base = import.meta.env.BASE_URL ?? '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  return `${normalizedBase}environments/${MODEL_VIEWER_SKYBOX_HDR_FILES[preset]}`;
}

function environmentPresetToAttr(
  preset: ModelViewerEnvironmentPreset
): Record<string, string> {
  if (preset === 'auto') return {};
  return { 'environment-image': preset };
}

/**
 * model-viewer only accepts image URLs for skybox-image — not "neutral"/"legacy"
 * preset strings (those are valid for environment-image only).
 */
function skyboxPresetToAttr(preset: ModelViewerSkyboxPreset): Record<string, string> {
  if (preset === 'none') {
    return { 'skybox-image': '' };
  }
  if (preset === 'auto') {
    return {};
  }
  return { 'skybox-image': resolveSkyboxHdrUrl(preset) };
}

export function resolvePresentationSettings(
  source?: Partial<ModelViewerSettings> | null
): ModelViewerPresentationSettings {
  return {
    shadowIntensity:
      source?.shadowIntensity ?? DEFAULT_MODEL_VIEWER_PRESENTATION.shadowIntensity,
    shadowSoftness:
      source?.shadowSoftness ?? DEFAULT_MODEL_VIEWER_PRESENTATION.shadowSoftness,
    exposure: source?.exposure ?? DEFAULT_MODEL_VIEWER_PRESENTATION.exposure,
    toneMapping:
      (source?.toneMapping as ModelViewerToneMapping | undefined) ??
      DEFAULT_MODEL_VIEWER_PRESENTATION.toneMapping,
    environmentImage:
      (source?.environmentImage as ModelViewerEnvironmentPreset | undefined) ??
      DEFAULT_MODEL_VIEWER_PRESENTATION.environmentImage,
    skyboxImage:
      (source?.skyboxImage as ModelViewerSkyboxPreset | undefined) ??
      DEFAULT_MODEL_VIEWER_PRESENTATION.skyboxImage,
    skyboxHeight: source?.skyboxHeight ?? DEFAULT_MODEL_VIEWER_PRESENTATION.skyboxHeight,
    backgroundColor:
      (source?.backgroundColor as ModelViewerBackgroundPreset | undefined) ??
      DEFAULT_MODEL_VIEWER_PRESENTATION.backgroundColor,
    autoRotate: source?.autoRotate ?? DEFAULT_MODEL_VIEWER_PRESENTATION.autoRotate,
    autoRotateDelay:
      source?.autoRotateDelay ?? DEFAULT_MODEL_VIEWER_PRESENTATION.autoRotateDelay,
    rotationPerSecond:
      (source?.rotationPerSecond as ModelViewerRotationSpeed | undefined) ??
      DEFAULT_MODEL_VIEWER_PRESENTATION.rotationPerSecond,
    variantName: source?.variantName ?? DEFAULT_MODEL_VIEWER_PRESENTATION.variantName,
  };
}

export function presentationSettingsToModelViewerSettings(
  presentation: ModelViewerPresentationSettings
): Pick<
  ModelViewerSettings,
  | 'shadowIntensity'
  | 'shadowSoftness'
  | 'exposure'
  | 'toneMapping'
  | 'environmentImage'
  | 'skyboxImage'
  | 'skyboxHeight'
  | 'backgroundColor'
  | 'autoRotate'
  | 'autoRotateDelay'
  | 'rotationPerSecond'
  | 'variantName'
> {
  return { ...presentation };
}

/** JSX spread attrs for `<model-viewer>`. */
export function presentationSettingsToAttrs(
  settings: ModelViewerPresentationSettings
): Record<string, string | boolean> {
  const attrs: Record<string, string | boolean> = {
    'shadow-intensity': String(settings.shadowIntensity),
    'shadow-softness': String(settings.shadowSoftness),
    exposure: String(settings.exposure),
    'tone-mapping': settings.toneMapping,
    ...environmentPresetToAttr(settings.environmentImage),
    ...skyboxPresetToAttr(settings.skyboxImage),
    'skybox-height': `${settings.skyboxHeight}m`,
    'rotation-per-second': settings.rotationPerSecond,
    'auto-rotate-delay': String(settings.autoRotateDelay),
  };

  if (settings.autoRotate) {
    attrs['auto-rotate'] = true;
  }

  if (settings.variantName.trim()) {
    attrs['variant-name'] = settings.variantName.trim();
  }

  return attrs;
}

export function presentationSettingsToViewerStyle(
  settings: ModelViewerPresentationSettings
): CSSProperties {
  if (settings.backgroundColor === 'transparent') {
    return { backgroundColor: 'transparent' };
  }
  return { backgroundColor: settings.backgroundColor };
}

/** @deprecated Use resolvePresentationSettings + presentationSettingsToAttrs instead. */
export const MODEL_VIEWER_PRESENTATION_ATTRS = presentationSettingsToAttrs(
  DEFAULT_MODEL_VIEWER_PRESENTATION
);
